// Reprise world buffs (REPRISE.md §5, phase R2). Each restored district
// grants a permanent passive to EVERY player on the server — the shared map
// is the build sheet, not a thermometer. Two tiers per district: a `base`
// value that applies to everyone the moment the district is restored, and an
// `amplified` value that replaces it for players who personally contributed
// Resonance or fragments to THAT district (tracked server-side in
// reprise_contributions). That rewards participation without gatekeeping the
// base tier from newcomers.
//
// Buffs are a FOURTH bonus bracket in Game.js, multiplying with the meta
// shop, character, and pet brackets — (1 + meta) × character × (1 + Σpets) ×
// (1 + Σworld) — matching the game's existing convention. Each multiplicative
// stat's world sum is capped (WORLD_MULT_CAP) so a fully restored map is a
// real shift without eclipsing progression the player earned.
//
// Every district id here must exist in supabase/reprise-schema.sql's
// start_reprise_season seed (repriseWorld.test.js pins the set). District ids
// with no buff entry simply grant nothing — safe, not an error.

import { REPRISE_TUNING } from './repriseTuning.js';

// Re-exported for callers/tests; the value lives in repriseTuning.js with the
// rest of the knobs.
export const WORLD_MULT_CAP = REPRISE_TUNING.worldMultCap; // max +% on any one multiplicative stat

// kind → how the value aggregates. Multiplicative stats sum then cap into a
// (1 + Σ) multiplier; `flat` sums directly; `level`/`choice` are small integer
// rule effects with clean Game.js seams (weapon start level, extra level-up
// pick).
const KINDS = {
  damage:    'mult',
  fireRate:  'mult',
  echoGain:  'mult',
  moveSpeed: 'mult',
  maxHp:     'mult',
  hpRegen:   'flat',
  weaponLevel: 'level',
  levelChoice: 'choice',
};

// One domain per Quarter, echoing the boss whose ruin defines it (REPRISE.md
// §2): the Empty Court restores authority (damage), the Measure restores
// timing (fire rate), the Riven Nave restores sound (Echo gain), the Long
// Table restores sustenance (max HP + regen), the Ashen Ward restores the
// forge (damage + weapon tier), the Second Crown restores reflection (move
// speed + the Loom's extra choice).
export const DISTRICT_BUFFS = {
  // The Empty Court — authority → weapon damage
  throne_steps:     { quarter: 'The Empty Court',  kind: 'damage', base: 0.03, amplified: 0.06, label: '+% weapon damage' },
  petitioners_hall: { quarter: 'The Empty Court',  kind: 'damage', base: 0.02, amplified: 0.04, label: '+% weapon damage' },
  seal_vault:       { quarter: 'The Empty Court',  kind: 'damage', base: 0.02, amplified: 0.04, label: '+% weapon damage' },
  heralds_gate:     { quarter: 'The Empty Court',  kind: 'damage', base: 0.03, amplified: 0.06, label: '+% weapon damage' },
  // The Measure — timing → fire rate
  clockworks:       { quarter: 'The Measure',      kind: 'fireRate', base: 0.03, amplified: 0.06, label: '+% fire rate' },
  metronome_yard:   { quarter: 'The Measure',      kind: 'fireRate', base: 0.02, amplified: 0.04, label: '+% fire rate' },
  bell_tower:       { quarter: 'The Measure',      kind: 'fireRate', base: 0.03, amplified: 0.06, label: '+% fire rate' },
  harvest_bells:    { quarter: 'The Measure',      kind: 'fireRate', base: 0.02, amplified: 0.04, label: '+% fire rate' },
  // The Riven Nave — sound → Echo gain
  conservatory:     { quarter: 'The Riven Nave',   kind: 'echoGain', base: 0.04, amplified: 0.08, label: '+% Echoes earned' },
  stave_bridge:     { quarter: 'The Riven Nave',   kind: 'echoGain', base: 0.03, amplified: 0.06, label: '+% Echoes earned' },
  echo_cistern:     { quarter: 'The Riven Nave',   kind: 'echoGain', base: 0.04, amplified: 0.08, label: '+% Echoes earned' },
  chorister_row:    { quarter: 'The Riven Nave',   kind: 'echoGain', base: 0.03, amplified: 0.06, label: '+% Echoes earned' },
  // The Long Table — sustenance → max HP and regen
  granary:          { quarter: 'The Long Table',   kind: 'maxHp',   base: 0.03, amplified: 0.06, label: '+% max HP' },
  aqueduct:         { quarter: 'The Long Table',   kind: 'hpRegen', base: 0.3,  amplified: 0.6,  label: 'HP regen /sec' },
  kitchens:         { quarter: 'The Long Table',   kind: 'maxHp',   base: 0.03, amplified: 0.06, label: '+% max HP' },
  almshouse:        { quarter: 'The Long Table',   kind: 'hpRegen', base: 0.2,  amplified: 0.4,  label: 'HP regen /sec' },
  // The Ashen Ward — forge → damage and weapon tier
  foundry:          { quarter: 'The Ashen Ward',   kind: 'weaponLevel', base: 1, amplified: 2, label: 'weapon starts higher' },
  kiln_row:         { quarter: 'The Ashen Ward',   kind: 'damage',  base: 0.03, amplified: 0.06, label: '+% weapon damage' },
  ember_docks:      { quarter: 'The Ashen Ward',   kind: 'damage',  base: 0.02, amplified: 0.04, label: '+% weapon damage' },
  slag_fields:      { quarter: 'The Ashen Ward',   kind: 'damage',  base: 0.03, amplified: 0.06, label: '+% weapon damage' },
  // The Second Crown — reflection → move speed, and the Loom's extra choice
  mirror_gallery:   { quarter: 'The Second Crown', kind: 'moveSpeed', base: 0.02, amplified: 0.04, label: '+% move speed' },
  archive:          { quarter: 'The Second Crown', kind: 'moveSpeed', base: 0.02, amplified: 0.04, label: '+% move speed' },
  the_loom:         { quarter: 'The Second Crown', kind: 'levelChoice', base: 1, amplified: 1, label: 'extra level-up choice' },
  regents_folly:    { quarter: 'The Second Crown', kind: 'moveSpeed', base: 0.02, amplified: 0.04, label: '+% move speed' },
};

function valueFor(id, contributedSet) {
  const def = DISTRICT_BUFFS[id];
  if (!def) return 0;
  return contributedSet.has(id) ? def.amplified : def.base;
}

/**
 * Aggregate the active world buffs into a bonus object Game.js applies at run
 * start. `restoredIds` are the districts currently restored (public server
 * state); `contributedIds` are the subset THIS player personally fed, which
 * upgrades those districts to their amplified tier. With no restored
 * districts this returns identity (all multipliers 1, all flats 0), so a run
 * with no world — offline, no season — is completely unchanged.
 */
export function getWorldBonuses(restoredIds = [], contributedIds = []) {
  const contributed = new Set(contributedIds);
  const sums = { damage: 0, fireRate: 0, echoGain: 0, moveSpeed: 0, maxHp: 0 };
  let hpRegen = 0;
  let weaponLevel = 0;
  let levelChoice = 0;

  for (const id of restoredIds) {
    const def = DISTRICT_BUFFS[id];
    if (!def) continue;
    const v = valueFor(id, contributed);
    switch (KINDS[def.kind]) {
      case 'mult': sums[def.kind] += v; break;
      case 'flat': hpRegen += v; break;
      case 'level': weaponLevel += v; break;
      case 'choice': levelChoice += v; break;
      default: break;
    }
  }

  const capped = (s) => 1 + Math.min(s, WORLD_MULT_CAP);
  return {
    damageMultiplier: capped(sums.damage),
    fireRateMultiplier: capped(sums.fireRate),
    echoGainMultiplier: capped(sums.echoGain),
    moveSpeedMultiplier: capped(sums.moveSpeed),
    maxHpMultiplier: capped(sums.maxHp),
    hpRegenPerSecond: hpRegen,
    weaponStartLevel: Math.min(weaponLevel, REPRISE_TUNING.weaponStartLevelCap),
    extraLevelUpChoices: Math.min(levelChoice, REPRISE_TUNING.extraLevelUpChoiceCap),
  };
}

// Counter-pressure (REPRISE.md §5): every restored district nudges the Court's
// remnants slightly stronger, server-wide. Net player power still climbs, but
// the season gains an arc — the world getting better and the enemies getting
// worse — and the finale lands against players who are strong, not bored.
// Small and capped so it never overtakes the buffs it accompanies (values in
// repriseTuning.js).
export function getCounterPressure(restoredCount = 0) {
  return 1 + Math.min(
    Math.max(0, restoredCount) * REPRISE_TUNING.counterPressurePerDistrict,
    REPRISE_TUNING.counterPressureCap,
  );
}

/**
 * Human-readable rollup of the active buffs, for the Reprise map UI. Groups
 * restored districts by their effect and reports the summed value, flagging
 * which are at their amplified tier for this player.
 */
export function summarizeWorldBuffs(restoredIds = [], contributedIds = []) {
  const b = getWorldBonuses(restoredIds, contributedIds);
  const amplified = new Set(contributedIds);
  const anyAmplified = restoredIds.some((id) => DISTRICT_BUFFS[id] && amplified.has(id));
  const lines = [];
  const pctLine = (mult, label) => {
    const p = Math.round((mult - 1) * 100);
    if (p > 0) lines.push(`+${p}% ${label}`);
  };
  pctLine(b.damageMultiplier, 'weapon damage');
  pctLine(b.fireRateMultiplier, 'fire rate');
  pctLine(b.echoGainMultiplier, 'Echoes earned');
  pctLine(b.moveSpeedMultiplier, 'move speed');
  pctLine(b.maxHpMultiplier, 'max HP');
  if (b.hpRegenPerSecond > 0) lines.push(`+${b.hpRegenPerSecond.toFixed(1)} HP/sec`);
  if (b.weaponStartLevel > 0) lines.push(`Weapons start +${b.weaponStartLevel} level`);
  if (b.extraLevelUpChoices > 0) lines.push(`+${b.extraLevelUpChoices} level-up choice`);
  return { lines, anyAmplified };
}
